import imageCompression from 'browser-image-compression';
import heic2any from 'heic2any';

export interface OptimizedImage {
  file: File;
  originalSize: number;
  optimizedSize: number;
  compressionRatio: number;
}

export class ImageOptimizer {
  private static readonly SUPPORTED_FORMATS = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/heic',
    'image/heif'
  ];

  private static readonly MAX_LONGEST_SIDE = 1024;
  private static readonly TARGET_QUALITY = 0.8;

  /**
   * Validates if the file is a supported image format
   */
  static isValidImageFile(file: File): boolean {
    // Check file extension for HEIC/HEIF since MIME type detection can be unreliable
    const fileName = file.name.toLowerCase();
    const hasValidExtension = fileName.endsWith('.jpg') || 
                             fileName.endsWith('.jpeg') || 
                             fileName.endsWith('.png') || 
                             fileName.endsWith('.heic') || 
                             fileName.endsWith('.heif');

    const hasValidMimeType = this.SUPPORTED_FORMATS.includes(file.type);
    
    // For standard image types, check both MIME type and extension
    if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png')) {
      return hasValidMimeType || hasValidExtension;
    }
    
    // For HEIC/HEIF, rely primarily on file extension since MIME type detection is unreliable
    return hasValidExtension;
  }

  /**
   * Converts HEIC/HEIF files to JPEG
   */
  private static async convertHeicToJpeg(file: File): Promise<File> {
    try {
      console.log('🔄 Converting HEIC/HEIF to JPEG...');
      
      // Try to convert using heic2any
      const result = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: this.TARGET_QUALITY
      });

      // heic2any can return an array or single blob, handle both cases
      const convertedBlob = Array.isArray(result) ? result[0] : result;

      const convertedFile = new File(
        [convertedBlob], 
        file.name.replace(/\.(heic|heif)$/i, '.jpg'),
        { type: 'image/jpeg' }
      );

      console.log('✅ HEIC/HEIF conversion complete');
      return convertedFile;
    } catch (error) {
      console.error('❌ HEIC/HEIF conversion failed:', error);
      
      // If conversion fails, we'll let the browser try to handle it as-is
      // Some modern browsers can handle HEIC files natively
      throw new Error(`HEIC_CONVERSION_FAILED:HEIC/HEIF conversion not supported in this browser. Please convert your image to JPG or PNG format before uploading, or try using a different browser.`);
    }
  }

  /**
   * Calculates the new dimensions maintaining aspect ratio
   */
  private static calculateNewDimensions(width: number, height: number): { width: number; height: number } {
    const longestSide = Math.max(width, height);
    
    if (longestSide <= this.MAX_LONGEST_SIDE) {
      return { width, height };
    }

    const ratio = this.MAX_LONGEST_SIDE / longestSide;
    return {
      width: Math.round(width * ratio),
      height: Math.round(height * ratio)
    };
  }

  /**
   * Optimizes an image by resizing and converting to JPEG
   */
  static async optimizeImage(file: File): Promise<OptimizedImage> {
    if (!this.isValidImageFile(file)) {
      throw new Error(`Unsupported file format. Supported formats: JPG, JPEG, PNG, HEIC, HEIF`);
    }

    console.log('🖼️ Starting image optimization...');
    console.log(`📊 Original file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);

    let processedFile = file;
    const originalSize = file.size;

    // Step 1: Convert HEIC/HEIF to JPEG if needed
    const fileName = file.name.toLowerCase();
    const isHeicFile = fileName.endsWith('.heic') || fileName.endsWith('.heif') || 
                       file.type === 'image/heic' || file.type === 'image/heif';
    
    if (isHeicFile) {
      try {
        processedFile = await this.convertHeicToJpeg(file);
      } catch (conversionError) {
        // If HEIC conversion fails, try to process the original file
        // Some browsers might be able to handle it natively
        console.warn('HEIC conversion failed, attempting to process original file:', conversionError);
        
        // Check if the browser can create an image element from this file
        try {
          await this.validateImageCanBeProcessed(file);
          processedFile = file; // Use original file if browser can handle it
          console.log('✅ Browser can handle HEIC file natively');
        } catch (validationError) {
          // If browser can't handle it either, throw the original conversion error
          throw conversionError;
        }
      }
    }

    // Step 2: Get image dimensions
    const imageDimensions = await this.getImageDimensions(processedFile);
    const newDimensions = this.calculateNewDimensions(imageDimensions.width, imageDimensions.height);
    
    console.log(`📐 Original dimensions: ${imageDimensions.width}x${imageDimensions.height}`);
    console.log(`📐 Target dimensions: ${newDimensions.width}x${newDimensions.height}`);

    // Step 3: Compress and resize the image
    const compressionOptions = {
      maxSizeMB: 10, // Max 10MB
      maxWidthOrHeight: this.MAX_LONGEST_SIDE,
      useWebWorker: true,
      fileType: 'image/jpeg' as const,
      quality: this.TARGET_QUALITY,
      alwaysKeepResolution: false
    };

    try {
      const compressedFile = await imageCompression(processedFile, compressionOptions);
      
      // Ensure the file has a .jpg extension
      const finalFileName = processedFile.name.replace(/\.(png|heic|heif|jpeg)$/i, '.jpg');
      const optimizedFile = new File([compressedFile], finalFileName, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });

      const optimizedSize = optimizedFile.size;
      const compressionRatio = ((originalSize - optimizedSize) / originalSize * 100);

      console.log(`✅ Optimization complete: ${(optimizedSize / 1024 / 1024).toFixed(2)} MB`);
      console.log(`📉 Compression ratio: ${compressionRatio.toFixed(1)}%`);

      return {
        file: optimizedFile,
        originalSize,
        optimizedSize,
        compressionRatio
      };
    } catch (error) {
      console.error('❌ Image optimization failed:', error);
      throw new Error('Failed to optimize image');
    }
  }

  /**
   * Gets the dimensions of an image file
   */
  private static getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      
      img.src = url;
    });
  }

  /**
   * Validates file size (max 50MB for uploads)
   */
  static validateFileSize(file: File, maxSizeMB: number = 50): boolean {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  }

  /**
   * Validates that the browser can process the image file
   */
  private static validateImageCanBeProcessed(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      const cleanup = () => URL.revokeObjectURL(url);
      
      img.onload = () => {
        cleanup();
        resolve();
      };
      
      img.onerror = () => {
        cleanup();
        reject(new Error('Browser cannot process this image format'));
      };
      
      // Set a timeout for the validation
      setTimeout(() => {
        cleanup();
        reject(new Error('Image validation timeout'));
      }, 5000);
      
      img.src = url;
    });
  }
} 